import type { HTMLAttributes, ReactNode } from "react";
import { cn } from "../lib/cn";

export type StatDirection = "up" | "down" | "flat";

export interface StatDelta {
  /** Formatted delta text, e.g. `+₩12,300` or `+2.14%`. */
  text: ReactNode;
  /** Drives color + arrow; keep it in sync with the sign of `text`. */
  direction: StatDirection;
  /** Small trailing note, e.g. `vs. deposits`. */
  hint?: ReactNode;
}

export interface StatProps extends Omit<HTMLAttributes<HTMLDivElement>, "title"> {
  label: ReactNode;
  value: ReactNode;
  /** De-emphasized caption under the value. */
  caption?: ReactNode;
  /** Optional signed delta chip (color + arrow, never color-alone). */
  delta?: StatDelta;
  /** Larger figure for hero tiles. */
  size?: "md" | "lg";
}

const DIRECTION_ARROW: Record<StatDirection, string> = {
  up: "▲",
  down: "▼",
  flat: "■",
};

const DIRECTION_CLASS: Record<StatDirection, string> = {
  up: "text-good-text",
  down: "text-critical",
  flat: "text-muted",
};

/**
 * A single KPI: label, a tabular-figures value, and an optional signed delta.
 * The delta pairs an arrow glyph with the color so direction survives CVD and
 * grayscale (per the palette's status rule).
 */
export function Stat({
  label,
  value,
  caption,
  delta,
  size = "md",
  className,
  ...rest
}: StatProps): ReactNode {
  return (
    <div className={cn("flex flex-col gap-1", className)} {...rest}>
      <span className="text-xs font-medium text-muted">{label}</span>
      <span
        className={cn(
          "font-semibold tabular-nums text-ink",
          size === "lg" ? "text-2xl" : "text-lg",
        )}
      >
        {value}
      </span>
      {delta !== undefined && (
        <span
          className={cn(
            "inline-flex items-center gap-1 text-xs font-medium tabular-nums",
            DIRECTION_CLASS[delta.direction],
          )}
        >
          <span aria-hidden="true">{DIRECTION_ARROW[delta.direction]}</span>
          <span>{delta.text}</span>
          {delta.hint !== undefined && <span className="text-muted">{delta.hint}</span>}
        </span>
      )}
      {caption !== undefined && <span className="text-xs text-muted">{caption}</span>}
    </div>
  );
}

/** Map a numeric sign (-1|0|1) to a {@link StatDirection}. */
export function directionFromSign(sign: number): StatDirection {
  if (sign > 0) return "up";
  if (sign < 0) return "down";
  return "flat";
}
